import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Search, ShieldOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function HanagroupAuditCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [radiusKm, setRadiusKm] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/maps/hanagroup-audit?radius_km=${radiusKm}`);
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'audit");
    }
    setLoading(false);
  };

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleLevel = (items) => {
    const ids = items.map(i => i.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer ${ids.length} prospect(s) sélectionné(s) ? Action irréversible.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post('/maps/hanagroup-audit/bulk-delete', { prospect_ids: ids });
      toast.success(`${data.deleted} prospect(s) supprimé(s)`);
      await runAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setDeleting(false);
  };

  const renderSection = (title, items, color, desc, testIdSuffix) => {
    if (!items) return null;
    const allSelected = items.length > 0 && items.every(i => selected.has(i.id));
    return (
      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`audit-section-${testIdSuffix}`}>
        <div className={`px-4 py-2.5 flex items-center gap-3 ${color}`}>
          <span className="font-semibold text-sm">{title}</span>
          <span className="ml-auto text-xs tabular-nums font-mono">{items.length}</span>
        </div>
        <div className="px-4 py-2 bg-white text-[11px] text-[#71717A] border-b border-[#E5E7EB]">
          {desc}
          {items.length > 0 && (
            <button onClick={() => toggleLevel(items)}
              className="ml-2 text-[#F97316] hover:underline"
              data-testid={`audit-toggle-all-${testIdSuffix}`}>
              {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-3 text-xs text-[#71717A] bg-white">Aucun prospect dans ce niveau. ✓</div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {items.map(it => (
              <label key={it.id} className="flex items-start gap-3 px-4 py-2 text-xs border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer"
                data-testid={`audit-item-${it.id}`}>
                <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)}
                  className="mt-0.5" data-testid={`audit-check-${it.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{it.enseigne} {it.nom_magasin}</div>
                  <div className="text-[#71717A]">{it.rue} · {it.code_postal} {it.ville}</div>
                  {it.matched_hanagroup && it.matched_hanagroup.length > 0 && (
                    <div className="text-[#DC2626] mt-0.5">
                      ↳ Hanagroup : {it.matched_hanagroup.map(m => `${m.enseigne} ${m.ville}`).join(', ')}
                      {it.distance_km != null && ` · ${it.distance_km} km`}
                    </div>
                  )}
                  {it.hanagroup_count_in_ville && (
                    <div className="text-[#A16207] mt-0.5">
                      ↳ {it.hanagroup_count_in_ville} point(s) Hanagroup dans {it.ville}
                      {it.hanagroup_enseignes_in_ville && ` (${it.hanagroup_enseignes_in_ville.join(', ')})`}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-[#71717A] uppercase">{it.statut_commercial}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="border-2 border-[#DC2626]/20 shadow-sm bg-gradient-to-br from-[#FEF2F2] to-white" data-testid="hanagroup-audit-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#DC2626]/15 flex items-center justify-center flex-shrink-0">
              <ShieldOff className="w-5 h-5 text-[#DC2626]" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Audit Hanagroup</h3>
              <p className="text-xs text-[#71717A] mt-0.5">
                Croise tous les prospects avec les positions interdites pour détecter d'éventuels doublons.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div>
              <Label className="text-[10px] text-[#71717A] block mb-0.5">Rayon GPS (km)</Label>
              <Input type="number" step="0.5" min="0.1" max="20" value={radiusKm}
                onChange={e => setRadiusKm(parseFloat(e.target.value) || 1)}
                className="h-8 w-20 text-xs" data-testid="audit-radius-input" />
            </div>
            <Button onClick={runAudit} disabled={loading}
              className="h-9 bg-[#DC2626] hover:bg-[#991B1B] text-white"
              data-testid="run-audit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Search className="w-4 h-4 mr-1.5" />}
              {loading ? 'Analyse...' : 'Lancer l\'audit'}
            </Button>
          </div>
        </div>

        {report && (
          <>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-2.5">
                <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Prospects</div>
                <div className="text-lg font-bold tabular-nums">{report.prospects_total}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-2.5">
                <div className="text-[10px] text-[#71717A] uppercase tracking-wider">Hanagroup</div>
                <div className="text-lg font-bold tabular-nums">{report.forbidden_total}</div>
              </div>
              <div className="bg-white border border-[#DC2626]/30 rounded-lg p-2.5">
                <div className="text-[10px] text-[#DC2626] uppercase tracking-wider font-semibold">Match exact</div>
                <div className="text-lg font-bold tabular-nums text-[#DC2626]">{report.counts.exact}</div>
              </div>
              <div className="bg-white border border-[#F59E0B]/30 rounded-lg p-2.5">
                <div className="text-[10px] text-[#B45309] uppercase tracking-wider font-semibold">Proximité GPS</div>
                <div className="text-lg font-bold tabular-nums text-[#B45309]">{report.counts.proximity}</div>
              </div>
            </div>

            {!report.gps_available && (
              <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-2.5 text-[11px] text-[#92400E]">
                ⚠️ Aucune coordonnée GPS sur vos prospects. Le niveau "Proximité GPS" ne peut pas être calculé — lancez un géocodage global depuis le bloc Géocodage ci-dessus.
              </div>
            )}

            <div className="space-y-2">
              {renderSection(
                '🔴 Doublons fermes (même enseigne + code postal + ville)',
                report.results.exact, 'bg-[#FEF2F2] text-[#991B1B]',
                'Prospects identifiés comme étant des points Hanagroup existants. À supprimer en priorité.',
                'exact',
              )}
              {renderSection(
                `🟠 Proximité GPS (< ${report.radius_km} km)`,
                report.results.proximity, 'bg-[#FFF7ED] text-[#9A3412]',
                'Prospects à vérifier manuellement : très proches géographiquement d\'un point Hanagroup.',
                'proximity',
              )}
              {renderSection(
                '🟡 Même ville (information)',
                report.results.ville, 'bg-[#FEFCE8] text-[#854D0E]',
                'Prospect d\'une enseigne DIFFÉRENTE mais située dans une ville où Hanagroup est déjà présent. Généralement sans impact.',
                'ville',
              )}
            </div>

            {selected.size > 0 && (
              <div className="flex items-center gap-3 p-3 bg-[#FEF2F2] border border-[#DC2626]/30 rounded-lg">
                <span className="text-sm font-medium text-[#991B1B] flex-1">
                  {selected.size} prospect(s) sélectionné(s)
                </span>
                <Button onClick={() => setSelected(new Set())} variant="outline" className="h-8 text-xs">
                  Effacer
                </Button>
                <Button onClick={bulkDelete} disabled={deleting}
                  className="h-8 bg-[#DC2626] hover:bg-[#991B1B] text-white text-xs"
                  data-testid="audit-bulk-delete-btn">
                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  Supprimer la sélection
                </Button>
              </div>
            )}

            <p className="text-[10px] text-[#A1A1AA]">
              Audit effectué à {new Date(report.checked_at).toLocaleString('fr-FR')}
            </p>
          </>
        )}

        {!report && !loading && (
          <div className="bg-white border border-dashed border-[#E5E7EB] rounded-lg p-6 text-center">
            <ShieldOff className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
            <p className="text-sm text-[#71717A]">Cliquez sur "Lancer l'audit" pour vérifier votre base.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
